import React, { ReactNode, useState, useMemo, useEffect, useRef } from 'react';
import { Sidebar } from './Sidebar';
import { MobileNavBar } from './MobileNavBar';
import { Logo } from './Logo';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, Bell, Check, Info, Briefcase, Trash2, Building2, Package, 
  Car, Wrench, FileText, Users, ClipboardList, Wifi, WifiOff, Database, RefreshCw,
  Search, X, CheckCircle2
} from 'lucide-react';
import { AIChatWidget } from './AIChatWidget';

interface LayoutProps {
  children: ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const isMobile = useIsMobile(768);
  const { currentTenant, currentUser, isPlatformAdmin, switchTenant, tenants } = useAuth();
  const { 
    notifications, markNotificationRead, deleteNotification, isOnline, pendingCount, isSyncing, lastSyncedAt, runSync,
    orders, offers, customers, inventory, vehicles, machines, employees
  } = useData() as any;
  const navigate = useNavigate();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSyncDashboard, setShowSyncDashboard] = useState(false);

  // Global Search State
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [showSearchPopover, setShowSearchPopover] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Global Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        setShowSearchPopover(true);
      } else if (e.key === 'Escape') {
        setShowSearchPopover(false);
        setShowNotifications(false);
        setShowSyncDashboard(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close search popover on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setShowSearchPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchResults = useMemo(() => {
    const query = globalSearchTerm.trim().toLowerCase();
    if (query.length < 2) return null;

    const matchedOrders = (orders || []).filter((o: any) => 
      (o.title || '').toLowerCase().includes(query) || (o.id || '').toLowerCase().includes(query) || (o.serviceAddress || '').toLowerCase().includes(query)
    ).slice(0, 4);

    const matchedOffers = (offers || []).filter((o: any) => 
      (o.id || '').toLowerCase().includes(query) || (o.customerName || '').toLowerCase().includes(query)
    ).slice(0, 4);

    const matchedCustomers = (customers || []).filter((c: any) => 
      (c.name || '').toLowerCase().includes(query) || (c.email || '').toLowerCase().includes(query) || (c.phone || '').toLowerCase().includes(query)
    ).slice(0, 4);

    const matchedInventory = (inventory || []).filter((i: any) => 
      (i.name || '').toLowerCase().includes(query)
    ).slice(0, 4);

    const matchedVehicles = (vehicles || []).filter((v: any) => 
      (v.name || '').toLowerCase().includes(query) || (v.licensePlate || '').toLowerCase().includes(query)
    ).slice(0, 4);

    const totalMatches = matchedOrders.length + matchedOffers.length + matchedCustomers.length + matchedInventory.length + matchedVehicles.length;

    return {
      orders: matchedOrders,
      offers: matchedOffers,
      customers: matchedCustomers,
      inventory: matchedInventory,
      vehicles: matchedVehicles,
      totalMatches
    };
  }, [globalSearchTerm, orders, offers, customers, inventory, vehicles]);

  // Filter notifications for the current user
  const myEmployeeRecord = (employees || []).find((e: any) => e.userId === currentUser?.id || e.id === currentUser?.id || e.email === (currentUser as any)?.email);
  const userNotifications = (notifications || []).filter((n: any) => 
    n.userId === currentUser?.id || 
    (myEmployeeRecord && (n.userId === myEmployeeRecord.id || n.userId === myEmployeeRecord.userId))
  );
  const unreadCount = userNotifications.filter((n: any) => !n.read).length;
  
  // Sort by date descending
  const sortedNotifications = [...userNotifications].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Navigate to the notification source
  const handleNotificationClick = (notif: any) => {
    if (!notif.read) markNotificationRead(notif.id);
    setShowNotifications(false);
    if (notif.orderId) {
      navigate(`/auftraege?highlight=${notif.orderId}`);
    } else if (notif.offerId) {
      navigate(`/angebote?highlight=${notif.offerId}`);
    } else if (notif.inventoryId) {
      navigate(`/lager?highlight=${notif.inventoryId}`);
    } else if (notif.vehicleId) {
      navigate(`/fuhrpark?highlight=${notif.vehicleId}`);
    } else if (notif.machineId) {
      navigate(`/maschinen?highlight=${notif.machineId}`);
    } else if (notif.customerId) {
      navigate(`/crm?highlight=${notif.customerId}`);
    }
  };

  const getNotifIcon = (notif: any) => {
    if (notif.type === 'order_assigned' || notif.type === 'dispo_assigned') return <ClipboardList size={17} />;
    if (notif.type === 'offer_accepted') return <FileText size={17} />;
    if (notif.orderId) return <Briefcase size={17} />;
    if (notif.inventoryId) return <Package size={17} />;
    if (notif.vehicleId) return <Car size={17} />;
    if (notif.machineId) return <Wrench size={17} />;
    if (notif.customerId) return <Users size={17} />;
    return <Info size={17} />;
  };

  const isClickable = (notif: any) => !!(notif.orderId || notif.offerId || notif.inventoryId || notif.vehicleId || notif.machineId || notif.customerId);

  return (
    <div className="app-container">
      {!isMobile && (
        <Sidebar isMobileOpen={isMobileSidebarOpen} closeMobile={() => setIsMobileSidebarOpen(false)} />
      )}
      <main className="main-content">
        <header className="top-bar" style={{
          backgroundColor: 'var(--ms-blue-primary)',
          color: '#ffffff',
          height: isMobile ? 'calc(50px + var(--sat))' : '48px',
          paddingTop: isMobile ? 'var(--sat)' : 0,
          paddingLeft: '16px',
          paddingRight: '16px',
          boxShadow: 'var(--sys-elevation-1)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          zIndex: 90
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            
            {/* Left: Mobile Logo & Title or Desktop Tenant Switcher */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
               {isMobile && (
                 <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                   <Logo size={24} />
                   <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ffffff', letterSpacing: '-0.2px' }}>
                     {currentTenant ? currentTenant.name : 'Betriebszentrale'}
                   </span>
                 </div>
               )}

               {!isMobile && (
                 <>
                   {isPlatformAdmin && tenants.length > 1 ? (
                     <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                       <Building2 size={16} color="#ffffff" />
                       <select
                         value={currentTenant?.id || ''}
                         onChange={e => switchTenant(e.target.value)}
                         style={{
                           fontSize: '0.85rem', 
                           fontWeight: 600, 
                           border: '1px solid rgba(255,255,255,0.3)',
                           background: 'rgba(255,255,255,0.18)',
                           color: '#ffffff',
                           padding: '4px 10px', 
                           borderRadius: '4px', 
                           cursor: 'pointer',
                           outline: 'none', 
                           maxWidth: '220px',
                           transition: 'background var(--transition-fast)'
                         }}
                       >
                         {tenants.map(t => (
                           <option key={t.id} value={t.id} style={{ color: 'var(--ms-text-primary)', background: 'var(--ms-bg-surface)' }}>{t.name}</option>
                         ))}
                       </select>
                     </div>
                   ) : (
                     <h1 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#ffffff', letterSpacing: '0.2px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                       {currentTenant ? currentTenant.name : 'Betriebszentrale'}
                     </h1>
                   )}
                 </>
               )}
            </div>

            {/* Middle: Desktop MS 365 Suite Global Search Bar with Shortcut */}
            {!isMobile && (
              <div ref={searchContainerRef} style={{
                flex: 1,
                maxWidth: '460px',
                margin: '0 20px',
                position: 'relative'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: globalSearchTerm || showSearchPopover ? 'var(--ms-bg-surface)' : 'rgba(255,255,255,0.18)',
                  border: '1px solid',
                  borderColor: showSearchPopover ? 'var(--ms-blue-primary)' : 'rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  padding: '0 10px',
                  height: '32px',
                  boxShadow: showSearchPopover ? 'var(--sys-elevation-2)' : 'none',
                  transition: 'all var(--transition-fast)'
                }}>
                  <Search 
                    size={15} 
                    style={{ 
                      color: globalSearchTerm || showSearchPopover ? 'var(--ms-blue-primary)' : 'rgba(255,255,255,0.85)', 
                      marginRight: '8px', 
                      flexShrink: 0 
                    }} 
                  />
                  <input 
                    ref={searchInputRef}
                    type="text" 
                    placeholder="Suchen in Betriebszentrale (Aufträge, Angebote, Kunden...)" 
                    value={globalSearchTerm}
                    onChange={e => {
                      setGlobalSearchTerm(e.target.value);
                      setShowSearchPopover(true);
                    }}
                    onFocus={() => setShowSearchPopover(true)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      outline: 'none',
                      color: globalSearchTerm || showSearchPopover ? 'var(--ms-text-primary)' : '#ffffff',
                      fontSize: '0.85rem',
                      width: '100%',
                      fontWeight: 400
                    }}
                  />
                  
                  {/* Clear button or ⌘K Hint */}
                  {globalSearchTerm ? (
                    <button 
                      onClick={() => { setGlobalSearchTerm(''); searchInputRef.current?.focus(); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ms-text-secondary)', padding: '2px', display: 'flex', alignItems: 'center' }}
                      title="Suche leeren"
                    >
                      <X size={14} />
                    </button>
                  ) : (
                    <kbd style={{
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      padding: '2px 5px',
                      borderRadius: '3px',
                      background: showSearchPopover ? 'var(--ms-bg-surface-secondary)' : 'rgba(255,255,255,0.22)',
                      color: showSearchPopover ? 'var(--ms-text-secondary)' : 'rgba(255,255,255,0.9)',
                      border: '1px solid',
                      borderColor: showSearchPopover ? 'var(--ms-border)' : 'rgba(255,255,255,0.35)',
                      fontFamily: 'inherit',
                      pointerEvents: 'none',
                      letterSpacing: '0.5px'
                    }}>
                      ⌘K
                    </kbd>
                  )}
                </div>

                {/* Live Search Results Popover (Theme-Harmonized) */}
                {showSearchPopover && globalSearchTerm.trim().length >= 2 && searchResults && (
                  <div 
                    className="animate-pop-in"
                    style={{
                      position: 'absolute',
                      top: '38px',
                      left: 0,
                      right: 0,
                      backgroundColor: 'var(--ms-bg-surface)',
                      color: 'var(--ms-text-primary)',
                      borderRadius: 'var(--sys-shape-corner-medium)',
                      boxShadow: 'var(--sys-elevation-3)',
                      border: '1px solid var(--ms-border)',
                      maxHeight: '440px',
                      overflowY: 'auto',
                      zIndex: 9999,
                      padding: '6px 0'
                    }}
                  >
                    {searchResults.totalMatches === 0 ? (
                      <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--ms-text-secondary)', fontSize: '0.85rem' }}>
                        <Search size={28} style={{ opacity: 0.3, marginBottom: '8px' }} />
                        <div>Keine Ergebnisse für "<strong>{globalSearchTerm}</strong>" gefunden.</div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {/* Orders */}
                        {searchResults.orders.length > 0 && (
                          <div>
                            <div style={{ padding: '6px 12px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--ms-blue-primary)', background: 'var(--ms-bg-surface-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <ClipboardList size={13} /> Aufträge ({searchResults.orders.length})
                            </div>
                            {searchResults.orders.map((o: any) => (
                              <div 
                                key={o.id} 
                                onClick={() => {
                                  navigate(`/auftraege?highlight=${o.id}`);
                                  setShowSearchPopover(false);
                                  setGlobalSearchTerm('');
                                }}
                                style={{ 
                                  padding: '8px 12px', 
                                  cursor: 'pointer', 
                                  borderBottom: '1px solid var(--ms-border-subtle)', 
                                  fontSize: '0.85rem',
                                  transition: 'background var(--transition-fast)'
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--ms-bg-surface-hover)')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                              >
                                <div style={{ fontWeight: 600, color: 'var(--ms-text-primary)' }}>{o.title || o.id}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--ms-text-secondary)', marginTop: '2px' }}>{o.id} · {o.serviceAddress || 'Keine Adresse'}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Offers */}
                        {searchResults.offers.length > 0 && (
                          <div>
                            <div style={{ padding: '6px 12px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--ms-blue-primary)', background: 'var(--ms-bg-surface-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <FileText size={13} /> Angebote ({searchResults.offers.length})
                            </div>
                            {searchResults.offers.map((o: any) => (
                              <div 
                                key={o.id} 
                                onClick={() => {
                                  navigate(`/angebote?highlight=${o.id}`);
                                  setShowSearchPopover(false);
                                  setGlobalSearchTerm('');
                                }}
                                style={{ 
                                  padding: '8px 12px', 
                                  cursor: 'pointer', 
                                  borderBottom: '1px solid var(--ms-border-subtle)', 
                                  fontSize: '0.85rem',
                                  transition: 'background var(--transition-fast)'
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--ms-bg-surface-hover)')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                              >
                                <div style={{ fontWeight: 600, color: 'var(--ms-text-primary)' }}>{o.id}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--ms-text-secondary)', marginTop: '2px' }} className="tabular-nums">Summe: {o.total?.toLocaleString('de-DE')} € · Status: {o.status}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Customers */}
                        {searchResults.customers.length > 0 && (
                          <div>
                            <div style={{ padding: '6px 12px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--ms-blue-primary)', background: 'var(--ms-bg-surface-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Users size={13} /> Kunden ({searchResults.customers.length})
                            </div>
                            {searchResults.customers.map((c: any) => (
                              <div 
                                key={c.id} 
                                onClick={() => {
                                  navigate(`/crm?highlight=${c.id}`);
                                  setShowSearchPopover(false);
                                  setGlobalSearchTerm('');
                                }}
                                style={{ 
                                  padding: '8px 12px', 
                                  cursor: 'pointer', 
                                  borderBottom: '1px solid var(--ms-border-subtle)', 
                                  fontSize: '0.85rem',
                                  transition: 'background var(--transition-fast)'
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--ms-bg-surface-hover)')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                              >
                                <div style={{ fontWeight: 600, color: 'var(--ms-text-primary)' }}>{c.name}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--ms-text-secondary)', marginTop: '2px' }}>{c.contact || c.email || c.phone}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Inventory */}
                        {searchResults.inventory.length > 0 && (
                          <div>
                            <div style={{ padding: '6px 12px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--ms-blue-primary)', background: 'var(--ms-bg-surface-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Package size={13} /> Lager / Material ({searchResults.inventory.length})
                            </div>
                            {searchResults.inventory.map((i: any) => (
                              <div 
                                key={i.id} 
                                onClick={() => {
                                  navigate(`/lager?highlight=${i.id}`);
                                  setShowSearchPopover(false);
                                  setGlobalSearchTerm('');
                                }}
                                style={{ 
                                  padding: '8px 12px', 
                                  cursor: 'pointer', 
                                  borderBottom: '1px solid var(--ms-border-subtle)', 
                                  fontSize: '0.85rem',
                                  transition: 'background var(--transition-fast)'
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--ms-bg-surface-hover)')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                              >
                                <div style={{ fontWeight: 600, color: 'var(--ms-text-primary)' }}>{i.name}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--ms-text-secondary)', marginTop: '2px' }} className="tabular-nums">Bestand: {i.stock} {i.unit}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Vehicles */}
                        {searchResults.vehicles.length > 0 && (
                          <div>
                            <div style={{ padding: '6px 12px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--ms-blue-primary)', background: 'var(--ms-bg-surface-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Car size={13} /> Fuhrpark ({searchResults.vehicles.length})
                            </div>
                            {searchResults.vehicles.map((v: any) => (
                              <div 
                                key={v.id} 
                                onClick={() => {
                                  navigate(`/fuhrpark?highlight=${v.id}`);
                                  setShowSearchPopover(false);
                                  setGlobalSearchTerm('');
                                }}
                                style={{ 
                                  padding: '8px 12px', 
                                  cursor: 'pointer', 
                                  borderBottom: '1px solid var(--ms-border-subtle)', 
                                  fontSize: '0.85rem',
                                  transition: 'background var(--transition-fast)'
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--ms-bg-surface-hover)')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                              >
                                <div style={{ fontWeight: 600, color: 'var(--ms-text-primary)' }}>{v.name} ({v.licensePlate})</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Right: Sync Status & Notifications & Beta Badge */}
            <div style={{ display: 'flex', gap: isMobile ? '6px' : '10px', alignItems: 'center' }}>
              
              {/* Mobile Quick Search Button */}
              {isMobile && (
                <button
                  className="btn subtle"
                  style={{
                    padding: '6px',
                    borderRadius: '4px',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: showSearchPopover ? 'rgba(255,255,255,0.2)' : 'transparent',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setShowSearchPopover(!showSearchPopover);
                    setShowNotifications(false);
                    setShowSyncDashboard(false);
                  }}
                  aria-label="Suche öffnen"
                >
                  <Search size={19} />
                </button>
              )}

              {/* Sync Dashboard Icon Button */}
              <div style={{ position: 'relative' }}>
                <button 
                  className="btn subtle" 
                  style={{ 
                    padding: '6px', 
                    position: 'relative', 
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#ffffff',
                    background: showSyncDashboard ? 'rgba(255,255,255,0.2)' : 'transparent'
                  }}
                  onClick={() => {
                    setShowSyncDashboard(!showSyncDashboard);
                    setShowNotifications(false);
                    setShowSearchPopover(false);
                  }}
                  title={isOnline ? "Sync-Status: Online" : "Sync-Status: Offline"}
                  aria-label="Sync Zentrale"
                >
                  {isOnline ? (
                    <Wifi size={18} color={pendingCount > 0 ? '#fcd34d' : '#ffffff'} />
                  ) : (
                    <WifiOff size={18} color="#fca5a5" />
                  )}
                  {pendingCount > 0 && (
                    <span style={{ 
                      position: 'absolute', 
                      top: 0, 
                      right: 0, 
                      backgroundColor: 'var(--ms-status-warning)', 
                      color: '#ffffff', 
                      fontSize: '0.62rem', 
                      fontWeight: 700, 
                      width: '15px', 
                      height: '15px', 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      {pendingCount}
                    </span>
                  )}
                </button>

                {/* Sync Dashboard Popover */}
                {showSyncDashboard && (
                  <div className="card animate-pop-in" style={{ 
                    position: 'absolute', 
                    top: '100%', 
                    right: 0, 
                    width: '320px', 
                    zIndex: 1000, 
                    marginTop: '8px', 
                    padding: '18px', 
                    boxShadow: 'var(--sys-elevation-3)',
                    background: 'var(--ms-bg-surface)',
                    borderRadius: 'var(--sys-shape-corner-medium)',
                    border: '1px solid var(--ms-border)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', borderBottom: '1px solid var(--ms-border)', paddingBottom: '10px' }}>
                      <Database size={17} color="var(--ms-blue-primary)" />
                      <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--ms-text-primary)' }}>Offline & Cloud Synchronisation</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* Connection status */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.825rem', color: 'var(--ms-text-secondary)' }}>Verbindung:</span>
                        <span className={`status-pill ${isOnline ? 'success' : 'error'}`}>
                          <span className="status-dot" style={{ backgroundColor: isOnline ? 'var(--ms-status-success)' : 'var(--ms-status-error)' }} />
                          {isOnline ? 'Online' : 'Offline'}
                        </span>
                      </div>

                      {/* Queue Status */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.825rem', color: 'var(--ms-text-secondary)' }}>Ausstehend:</span>
                        <span style={{ 
                          fontSize: '0.85rem', 
                          fontWeight: 700,
                          color: pendingCount > 0 ? 'var(--ms-status-warning)' : 'var(--ms-status-success)'
                        }} className="tabular-nums">
                          {pendingCount} Eintr{pendingCount === 1 ? 'ag' : 'äge'}
                        </span>
                      </div>

                      {/* Last Sync */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.825rem', color: 'var(--ms-text-secondary)' }}>Letzter Sync:</span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--ms-text-primary)' }} className="tabular-nums">
                          {lastSyncedAt ? lastSyncedAt.toLocaleTimeString('de-DE') : 'Bisher kein Sync'}
                        </span>
                      </div>

                      {/* Sync Trigger button */}
                      <button 
                        disabled={isSyncing || !isOnline}
                        onClick={runSync}
                        className="btn primary"
                        style={{
                          width: '100%',
                          marginTop: '6px',
                          height: '36px',
                          opacity: (isSyncing || !isOnline) ? 0.6 : 1,
                          cursor: (isSyncing || !isOnline) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <RefreshCw size={15} style={{
                          animation: isSyncing ? 'spin 1s linear infinite' : 'none'
                        }} />
                        {isSyncing ? 'Synchronisiere...' : 'Jetzt synchronisieren'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Notifications Icon Button */}
              <div style={{ position: 'relative' }}>
                <button 
                  className="btn subtle" 
                  style={{ 
                    padding: '6px', 
                    position: 'relative', 
                    borderRadius: '4px',
                    color: '#ffffff',
                    background: showNotifications ? 'rgba(255,255,255,0.2)' : 'transparent',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                    setShowSyncDashboard(false);
                  }}
                  aria-label="Benachrichtigungen"
                >
                  <Bell size={18} color="#ffffff" />
                  {unreadCount > 0 && (
                    <span style={{ 
                      position: 'absolute', 
                      top: 0, 
                      right: 0, 
                      backgroundColor: 'var(--ms-status-error)', 
                      color: '#ffffff', 
                      fontSize: '0.62rem', 
                      fontWeight: 700, 
                      width: '16px', 
                      height: '16px', 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Popover */}
                {showNotifications && (
                  <div className="card animate-pop-in" style={{ 
                    position: 'absolute', 
                    top: '100%', 
                    right: 0, 
                    width: '360px', 
                    zIndex: 1000, 
                    marginTop: '8px', 
                    padding: 0, 
                    overflow: 'hidden', 
                    boxShadow: 'var(--sys-elevation-3)',
                    background: 'var(--ms-bg-surface)',
                    border: '1px solid var(--ms-border)'
                  }}>
                    <div style={{ 
                      padding: '12px 16px', 
                      borderBottom: '1px solid var(--ms-border)', 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      backgroundColor: 'var(--ms-bg-surface-secondary)' 
                    }}>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--ms-text-primary)' }}>Benachrichtigungen</span>
                      {unreadCount > 0 && (
                        <button 
                          onClick={() => sortedNotifications.filter((n: any) => !n.read).forEach((n: any) => markNotificationRead(n.id))}
                          style={{ fontSize: '0.75rem', background: 'none', border: 'none', color: 'var(--ms-blue-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}
                        >
                          <Check size={14} /> Alle gelesen
                        </button>
                      )}
                    </div>
                    <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                      {sortedNotifications.length === 0 ? (
                        <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--ms-text-secondary)', fontSize: '0.85rem' }}>
                          <CheckCircle2 size={32} color="var(--ms-status-success)" style={{ opacity: 0.6, margin: '0 auto 8px auto' }} />
                          <div style={{ fontWeight: 600 }}>Alles auf dem neuesten Stand</div>
                          <div style={{ fontSize: '0.78rem', marginTop: '2px' }}>Keine neuen Benachrichtigungen vorhanden.</div>
                        </div>
                      ) : (
                        sortedNotifications.slice(0, 20).map((notif: any) => (
                          <div 
                            key={notif.id} 
                            style={{ 
                              padding: '12px 16px', 
                              borderBottom: '1px solid var(--ms-border-subtle)',
                              backgroundColor: notif.read ? 'transparent' : 'var(--ms-blue-light)',
                              cursor: isClickable(notif) ? 'pointer' : 'default',
                              display: 'flex',
                              gap: '12px',
                              transition: 'background var(--transition-fast)'
                            }}
                            onClick={() => handleNotificationClick(notif)}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = notif.read ? 'var(--ms-bg-surface-hover)' : 'var(--ms-blue-subtle)')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = notif.read ? 'transparent' : 'var(--ms-blue-light)')}
                          >
                            <div style={{ marginTop: '2px', color: notif.read ? 'var(--ms-text-secondary)' : 'var(--ms-blue-primary)', flexShrink: 0 }}>
                              {getNotifIcon(notif)}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: notif.read ? 500 : 600, fontSize: '0.875rem', color: 'var(--ms-text-primary)', marginBottom: '3px' }}>{notif.title}</div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--ms-text-secondary)', lineHeight: 1.4 }}>{notif.message}</div>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                                <span style={{ fontSize: '0.7rem', color: 'var(--ms-text-muted)' }} className="tabular-nums">
                                  {new Date(notif.createdAt).toLocaleString('de-DE')}
                                </span>
                                {isClickable(notif) && (
                                  <span style={{ fontSize: '0.72rem', color: 'var(--ms-blue-primary)', fontWeight: 600 }}>
                                    Anzeigen →
                                  </span>
                                )}
                              </div>
                            </div>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notif.id);
                              }}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                                color: 'var(--ms-text-muted)', alignSelf: 'flex-start'
                              }}
                              title="Benachrichtigung löschen"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Status Badge */}
              <span className="ms-badge info" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.3)', fontWeight: 700 }}>
                Enterprise
              </span>
            </div>
          </div>
        </header>

        <div className="page-content">
          {children}
        </div>
      </main>

      {/* Mobile Full-Screen Search Modal */}
      {isMobile && showSearchPopover && (
        <div className="mobile-menu-sheet" onClick={() => setShowSearchPopover(false)}>
          <div className="mobile-menu-content" onClick={e => e.stopPropagation()} style={{ height: '80vh' }}>
            <div className="mobile-sheet-handle" />

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'var(--ms-bg-surface-secondary)',
                border: '1px solid var(--ms-blue-primary)',
                borderRadius: '8px',
                padding: '0 12px',
                height: '42px'
              }}>
                <Search size={18} color="var(--ms-blue-primary)" style={{ marginRight: '8px' }} />
                <input
                  autoFocus
                  type="text"
                  placeholder="Auftrag, Kunde, Angebot..."
                  value={globalSearchTerm}
                  onChange={e => setGlobalSearchTerm(e.target.value)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    color: 'var(--ms-text-primary)',
                    fontSize: '16px',
                    width: '100%'
                  }}
                />
                {globalSearchTerm && (
                  <button 
                    onClick={() => setGlobalSearchTerm('')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ms-text-secondary)' }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <button 
                onClick={() => setShowSearchPopover(false)}
                className="btn secondary"
                style={{ height: '42px', padding: '0 12px' }}
              >
                Fertig
              </button>
            </div>

            {/* Mobile Search Results */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {globalSearchTerm.trim().length < 2 ? (
                <div style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--ms-text-secondary)', fontSize: '0.85rem' }}>
                  Mindestens 2 Zeichen eingeben, um Aufträge, Kunden oder Angebote zu durchsuchen.
                </div>
              ) : searchResults?.totalMatches === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--ms-text-secondary)', fontSize: '0.85rem' }}>
                  Keine Treffer für "{globalSearchTerm}".
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {searchResults?.orders.map((o: any) => (
                    <div
                      key={o.id}
                      onClick={() => {
                        setShowSearchPopover(false);
                        navigate(`/auftraege?highlight=${o.id}`);
                      }}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        background: 'var(--ms-bg-surface-secondary)',
                        border: '1px solid var(--ms-border)'
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ms-text-primary)' }}>{o.title || o.id}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--ms-text-secondary)', marginTop: '2px' }}>{o.id} · {o.serviceAddress || 'Adresse offen'}</div>
                    </div>
                  ))}
                  {searchResults?.customers.map((c: any) => (
                    <div
                      key={c.id}
                      onClick={() => {
                        setShowSearchPopover(false);
                        navigate(`/crm?highlight=${c.id}`);
                      }}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        background: 'var(--ms-bg-surface-secondary)',
                        border: '1px solid var(--ms-border)'
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--ms-text-primary)' }}>{c.name}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--ms-text-secondary)', marginTop: '2px' }}>{c.contact || c.phone || c.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Floating Offline Sync Indicator Widget (Adjusted for mobile tab bar) */}
      {(!isOnline || pendingCount > 0) && (
        <div 
          className="animate-pop-in"
          style={{
            position: 'fixed',
            bottom: isMobile ? 'calc(70px + var(--sab))' : '24px',
            right: isMobile ? '16px' : '90px',
            left: isMobile ? '16px' : 'auto',
            zIndex: 9999,
            background: !isOnline 
              ? 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)' 
              : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            padding: '10px 18px',
            borderRadius: isMobile ? '12px' : 'var(--sys-shape-corner-full)',
            boxShadow: 'var(--sys-elevation-4)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            fontFamily: 'inherit',
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: isOnline ? 'pointer' : 'default'
          }}
          onClick={() => {
            if (isOnline) {
              runSync();
            }
          }}
          title={isOnline ? "Klicken, um ausstehende Daten zu synchronisieren." : "Offline-Modus aktiv."}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {isOnline ? <Database size={17} /> : <WifiOff size={17} />}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.3px' }}>
                {!isOnline ? 'OFFLINE-MODUS AKTIV' : 'UNSYNCHRONISIERTE DATEN'}
              </span>
              <span style={{ fontSize: '0.72rem', fontWeight: 500, opacity: 0.95 }} className="tabular-nums">
                {!isOnline 
                  ? `${pendingCount} Änderung${pendingCount === 1 ? '' : 'en'} lokal gesichert`
                  : `${pendingCount} Änderung${pendingCount === 1 ? '' : 'en'} ausstehend – Jetzt synchronisieren`
                }
              </span>
            </div>
          </div>

          {isOnline && (
            <button style={{
              background: 'rgba(255,255,255,0.22)',
              border: 'none',
              borderRadius: '50%',
              width: '26px',
              height: '26px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              cursor: 'pointer'
            }}>
              <RefreshCw size={13} style={{
                animation: isSyncing ? 'spin 1s linear infinite' : 'none'
              }} />
            </button>
          )}
        </div>
      )}
      
      {/* AI Chat Widget — only shown when 'ki' module is active for tenant AND user */}
      {(isPlatformAdmin ||
        ((currentTenant?.activeModules || []).includes('ki') &&
         (currentUser?.activeModules || []).includes('ki'))
      ) && <AIChatWidget />}

      {/* iOS Native Bottom Navigation Bar */}
      {isMobile && <MobileNavBar />}
    </div>
  );
};
